import { useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { updateScene } from '@/store/slices/room';

import logger from '@/utils/logger';
import { APP_CONFIG } from '@/config';

// 模块级去重：避免多个组件并发调用该 Hook 导致重复日志/dispatch
let lastAppliedSceneId: string | null = null;
let lastSyncedURLScene: string | null = null;

/**
 * 处理URL参数中场景选择的Hook
 * @returns {object} URL中的场景ID，store中的当前场景，以及导航函数
 */
export const useSceneFromURL = () => {
  const { sceneId } = useParams<{ sceneId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { scene } = useSelector((state: RootState) => state.room);

  // 获取URL参数中的问题
  const getQuestionFromURL = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('Question');
  }, [location.search]);

  // 获取URL参数中的自动开始标志
  const getAutoStartFromURL = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('AutoStart') === 'true';
  }, [location.search]);

  // 获取URL参数中的直接进入Room标志
  const getDirectRoomFromURL = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('DirectRoom') === 'true';
  }, [location.search]);

  // 导航到场景URL
  const navigateToScene = useCallback(
    (targetSceneId: string) => {
      if (targetSceneId && targetSceneId !== sceneId) {
        // 保留当前URL参数
        const currentParams = new URLSearchParams(location.search);
        const paramString = currentParams.toString();
        const targetUrl = paramString
          ? `/scene/${targetSceneId}?${paramString}`
          : `/scene/${targetSceneId}`;
        navigate(targetUrl, { replace: true });
      }
    },
    [navigate, sceneId, location.search]
  );

  // 导航到主页（默认场景）
  const navigateToHome = useCallback(() => {
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
    }
  }, [navigate, location.pathname]);

  // 从URL参数设置场景（不依赖配置是否已加载，先写入 scene，配置随后同步）
  useEffect(() => {
    // 1) URL 显式带场景：以 URL 为准
    if (sceneId) {
      if (sceneId !== scene && lastAppliedSceneId !== sceneId) {
        lastAppliedSceneId = sceneId;
        logger.info(`[SceneURL] 从URL设置场景: ${sceneId}`);
        dispatch(updateScene(sceneId));
      }
      return;
    }

    // 2) URL 未带场景：若 store 也没有场景，则跳转到默认场景
    //    默认场景从 APP_CONFIG.defaultSceneId 读取
    if (!scene && APP_CONFIG.defaultSceneId) {
      const target = APP_CONFIG.defaultSceneId;
      if (lastAppliedSceneId !== target) {
        lastAppliedSceneId = target;
        logger.info(`[SceneURL] 无场景指定，跳转到默认场景: ${target}`);
        dispatch(updateScene(target));
        navigate(`/scene/${encodeURIComponent(target)}`, { replace: true });
      }
    }
  }, [sceneId, scene, dispatch]);

  // 当store中的场景变化时更新URL
  useEffect(() => {
    if (scene && scene !== sceneId) {
      if (lastSyncedURLScene !== scene) {
        lastSyncedURLScene = scene;
        logger.info(`[SceneURL] 场景变更，更新URL: ${scene}`);
      }
      navigateToScene(scene);
    }
  }, [scene, sceneId, navigateToScene]);

  return {
    sceneId,
    scene,
    navigateToScene,
    navigateToHome,
    isSceneFromURL: !!sceneId,
    questionFromURL: getQuestionFromURL(),
    autoStartFromURL: getAutoStartFromURL(),
    directRoomFromURL: getDirectRoomFromURL(),
  };
};
